import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { ReactNode } from 'react';

import { cn } from '../../utils/cn';
import { Text } from '../Text';

export type TooltipProps = {
  title?: string | ReactNode;
  description?: string;
  children: ReactNode;
  tooltipClassName?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  invert?: boolean;
  delayDuration?: number;
  disabled?: boolean;
  blockTriggerEvents?: boolean;
};

export function Tooltip({
  title,
  description,
  children,
  tooltipClassName,
  side = 'top',
  sideOffset = 10,
  align = 'center',
  alignOffset = 0,
  invert = false,
  delayDuration = 0,
  disabled = false,
  blockTriggerEvents = false,
}: TooltipProps) {
  if ((!title && !description) || disabled) {
    return children;
  }

  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger
        asChild
        onPointerDown={(e) => {
          if (blockTriggerEvents) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          sideOffset={sideOffset}
          side={side}
          align={align}
          alignOffset={alignOffset}
          className={cn(
            'z-[1500] max-w-[320px] overflow-hidden rounded-md border border-secondary bg-background p-2 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            tooltipClassName,
            invert && 'invert'
          )}
        >
          <div className="flex flex-col gap-2 text-xxs">
            {typeof title === 'string' ? (
              <Text
                variant="sm"
                data-target="title"
                className={!tooltipClassName ? 'text-primary' : ''}
              >
                {title}
              </Text>
            ) : (
              title
            )}
            {description && (
              <Text
                variant="sm"
                className={!tooltipClassName ? 'text-secondary' : ''}
                data-target="description"
              >
                {description}
              </Text>
            )}
          </div>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
